import { useState } from "react";
import { CheckCircle2, Download, RotateCcw, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { useI18n } from "../../../i18n";
import { useAuth } from "../../../context/AuthContext";
import { api } from "../../../lib/api";
import { fmtDateTime, fmtMoney, todayAthens } from "../../../lib/format";
import { hasPerm } from "../../../lib/perm";
import { STATUS_CLS, payErr } from "../../../lib/payments";
import { downloadFile } from "../../../lib/xutil";
import { useApi } from "../../customer/hooks";
import { Field, PageHead, Panel, btnGhost, btnPrimary, chip, input } from "../../../components/common/Page";
import { DateField } from "../../../components/common/DateField";
import { EmptyState, SkeletonBlock } from "../../../components/common/EmptyState";

const RefundDialog = ({ p, onClose, onDone }) => {
  const { t } = useI18n();
  const [f, setF] = useState({ amount: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await api.post(`/staff/payments/${p.id}/refund`, { amount: f.amount ? Number(f.amount.replace(",", ".")) : null, reason: f.reason });
      toast.success(t("x.pay.refundDone")); onDone();
    } catch (e) { toast.error(payErr(t, e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="refund-dialog">
        <DialogHeader><DialogTitle>{t("x.pay.refundTitle")} · {p.customer_name} · {fmtMoney(p.total - (p.refunded_amount || 0))}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label={t("x.pay.refundAmount")}><input inputMode="decimal" value={f.amount} onChange={(e) => setF((s) => ({ ...s, amount: e.target.value }))} className={input} data-testid="refund-amount" /></Field>
          <Field label={t("x.pay.reason")}><input value={f.reason} onChange={(e) => setF((s) => ({ ...s, reason: e.target.value }))} className={input} data-testid="refund-reason" /></Field>
          <button type="button" disabled={busy || f.reason.trim().length < 3} onClick={go} className={`${btnPrimary} w-full`} data-testid="refund-submit">{t("x.pay.refund")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ p, canRefund, onRefund, onRetry }) => {
  const { t } = useI18n();
  return (
    <li className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm" data-testid={`staff-payment-${p.id}`}>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-navy">{p.customer_name} <span className="text-xs font-normal text-slate-500">{p.customer_code}</span></p>
        <p className="text-xs text-slate-500">{fmtDateTime(p.created_at)} · {t(`x.pay.${p.method}`)} · {p.receipt_no || p.provider_ref}{p.softone_receipt_no ? ` · SoftOne ${p.softone_receipt_no}` : ""}</p>
        {p.softone_status === "error" && <p className="text-xs text-rose-600">SoftOne: {p.softone_error}</p>}
      </div>
      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_CLS[p.status]}`}>{t(`x.pay.status.${p.status}`)}</span>
      <div className="w-28 text-right"><p className="font-bold tabular-nums text-navy">{fmtMoney(p.total)}</p>{p.refunded_amount > 0 && <p className="text-xs text-sky-700">−{fmtMoney(p.refunded_amount)}</p>}</div>
      {canRefund && p.status === "paid" && !["softone_link", "iris"].includes(p.method) && <button type="button" onClick={() => onRefund(p)} className={`${btnGhost} px-3`} data-testid={`refund-${p.id}`}><Undo2 className="h-4 w-4" />{t("x.pay.refund")}</button>}
      {canRefund && p.method === "iris" && p.status === "pending" && <button type="button" onClick={() => window.confirm(`${t("x.pay.markReceived")}: ${p.provider_ref} · ${fmtMoney(p.total)}?`) && onRetry(p, "mark-received")} className={`${btnGhost} px-3`} data-testid={`mark-received-${p.id}`}><CheckCircle2 className="h-4 w-4" />{t("x.pay.markReceived")}</button>}
      {canRefund && p.softone_status === "error" && <button type="button" onClick={() => onRetry(p)} className={`${btnGhost} px-3`} data-testid={`retry-softone-${p.id}`}><RotateCcw className="h-4 w-4" />{t("x.pay.retrySoftone")}</button>}
    </li>
  );
};

const Recon = () => {
  const { t } = useI18n();
  const [day, setDay] = useState(todayAthens());
  const r = useApi("recon", "/staff/payments/reconciliation", { day }, { staleTime: 0 });
  const d = r.data;
  return (
    <Panel title={t("x.pay.recon")} action={<DateField value={day} onChange={setDay} testId="recon-day" />} testId="recon-panel">
      {!d ? <SkeletonBlock className="h-24" /> : <>
        <div className="mb-3 grid grid-cols-3 gap-2 text-center text-sm">
          {[["app", d.totals.app], ["softone", d.totals.softone], ["provider", d.totals.provider]].map(([k, v]) => (
            <div key={k} className="rounded-xl bg-slate-50 p-2"><p className="text-xs text-slate-500">{k === "softone" ? "SoftOne" : t(`x.pay.${k}`)}</p><p className="font-bold tabular-nums text-navy" data-testid={`recon-total-${k}`}>{fmtMoney(v)}</p></div>
          ))}
        </div>
        <div className="overflow-x-auto"><table className="w-full min-w-[640px] text-sm" data-testid="recon-table">
          <thead><tr className="text-left text-xs text-slate-500"><th className="py-2">{t("x.pay.customer")}</th><th>{t("x.pay.method")}</th><th className="text-right">{t("x.pay.app")}</th><th className="text-right">SoftOne</th><th className="text-right">{t("x.pay.provider")}</th><th>{t("x.pay.payout")}</th><th /></tr></thead>
          <tbody className="divide-y divide-slate-100">{d.rows.map((x) => (
            <tr key={x.payment_id}><td className="py-2">{x.customer_name}</td><td>{t(`x.pay.${x.method}`)}</td><td className="text-right tabular-nums">{fmtMoney(x.app_total)}</td>
              <td className="text-right tabular-nums">{x.softone_amount == null ? "—" : fmtMoney(x.softone_amount)}</td><td className="text-right tabular-nums">{x.provider_gross == null ? "—" : fmtMoney(x.provider_gross)}</td>
              <td className="text-xs">{x.payout}</td><td><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${x.status === "matched" ? "bg-brand/25 text-navy" : "bg-rose-100 text-rose-700"}`}>{t(`x.pay.${x.status}`)}</span></td></tr>
          ))}</tbody>
        </table></div>
        {!d.rows.length && <p className="py-4 text-center text-sm text-slate-500">{t("x.pay.none")}</p>}
        {d.softone_without_app.length > 0 && <p className="mt-3 text-xs text-rose-600">{t("x.pay.orphans")}: {d.softone_without_app.map((o) => o.receipt_no).join(", ")}</p>}
      </>}
    </Panel>
  );
};

export default function Payments() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [tab, setTab] = useState("list");
  const [f, setF] = useState({ status: "", method: "", customer: "", date_from: "", date_to: "" });
  const [refund, setRefund] = useState(null);
  const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
  const r = useApi("staffPayments", "/staff/payments", params, { staleTime: 0 });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));
  const retry = (p, action = "retry-softone") => api.post(`/staff/payments/${p.id}/${action}`).then(() => r.refetch()).catch((e) => toast.error(payErr(t, e)));
  return (
    <div className="space-y-5" data-testid="staff-payments-page">
      <PageHead title={t("x.pay.staffTitle")} sub={t("x.pay.staffSub")}>
        {["list", "recon"].map((k) => <button key={k} type="button" className={chip(tab === k)} onClick={() => setTab(k)} data-testid={`payments-tab-${k}`}>{t(`x.pay.${k}`)}</button>)}
        <button type="button" onClick={() => downloadFile("/staff/payments/export.xlsx", params, "payments.xlsx")} className={btnGhost} data-testid="payments-export"><Download className="h-4 w-4" />{t("x.pay.export")}</button>
      </PageHead>
      {r.data && !r.data.customers_enabled && <p className="rounded-xl bg-amber-50 px-4 py-2 text-xs font-medium text-amber-800" data-testid="payments-hidden-note">{t("x.pay.hiddenProd")}</p>}
      {tab === "recon" ? <Recon /> : <>
        <div className="grid gap-2 sm:grid-cols-5">
          <select value={f.status} onChange={set("status")} className={input} data-testid="payments-filter-status"><option value="">{t("x.pay.allStatus")}</option>{["pending", "paid", "failed", "refunded", "expired"].map((s) => <option key={s} value={s}>{t(`x.pay.status.${s}`)}</option>)}</select>
          <select value={f.method} onChange={set("method")} className={input} data-testid="payments-filter-method"><option value="">{t("x.pay.allMethods")}</option>{["card", "iris", "softone_link"].map((s) => <option key={s} value={s}>{t(`x.pay.${s}`)}</option>)}</select>
          <input value={f.customer} onChange={set("customer")} placeholder={`${t("x.pay.customer")} (T0001)`} className={input} data-testid="payments-filter-customer" />
          <DateField value={f.date_from} onChange={set("date_from")} testId="payments-filter-from" />
          <DateField value={f.date_to} onChange={set("date_to")} testId="payments-filter-to" />
        </div>
        {r.isLoading ? <SkeletonBlock className="h-64" /> : !r.data?.items?.length ? <EmptyState title={t("x.pay.none")} testId="payments-empty" /> : (
          <ul className="divide-y divide-slate-100 overflow-hidden rounded-2xl bg-white shadow-sm" data-testid="payments-list">
            {r.data.items.map((p) => <Row key={p.id} p={p} canRefund={hasPerm(user, "payments:refund")} onRefund={setRefund} onRetry={retry} />)}
          </ul>
        )}
      </>}
      {refund && <RefundDialog p={refund} onClose={() => setRefund(null)} onDone={() => { setRefund(null); r.refetch(); }} />}
    </div>
  );
}
